import React from 'react';
import { Popover, Tooltip } from 'antd';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import s from './ClientTags.module.less';

type Props = {
    tags: string[];
    maxVisible?: number;
    enableDnd?: boolean;
    onReorder?: (next: string[]) => void;
    onAddClick?: () => void;
    onTagAction?: (tag: string) => void;
    tagActionText?: string;
    onMoreClick?: () => void;
    /** @deprecated use maxVisible instead */
    maxRows?: number;
};

function PlusIcon() {
    return (
        <svg className={s.plusIcon} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <line x1="6" y1="2" x2="6" y2="10" />
            <line x1="2" y1="6" x2="10" y2="6" />
        </svg>
    );
}

function SortableTag({ id }: Readonly<{ id: string }>) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    return (
        <span
            ref={setNodeRef}
            style={style}
            className={`${s.tag} ${isDragging ? s.tagDragging : ''}`}
            {...attributes}
            {...listeners}
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            {id}
        </span>
    );
}

export default function ClientTags(props: Readonly<Props>) {
    const {
        tags,
        maxVisible = 4,
        enableDnd = false,
        onReorder,
        onAddClick,
        onTagAction,
        tagActionText,
        onMoreClick,
    } = props;
    const [activeId, setActiveId] = React.useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 },
        }),
    );

    const handleDragEnd = React.useCallback(
        (event: DragEndEvent) => {
            setActiveId(null);
            if (!onReorder) return;
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            const oldIndex = tags.indexOf(String(active.id));
            const newIndex = tags.indexOf(String(over.id));
            if (oldIndex < 0 || newIndex < 0) return;
            const next = arrayMove(tags, oldIndex, newIndex);
            onReorder(next);
        },
        [onReorder, tags],
    );

    const actionText = tagActionText || 'Remove tag';
    const visibleTags = tags.slice(0, maxVisible);
    const hiddenTags = tags.slice(maxVisible);
    const hiddenCount = hiddenTags.length;

    const addButton = (
        <div
            className={s.tagAdd}
            onClick={(e) => {
                e.stopPropagation();
                onAddClick?.();
            }}
            role="button"
            tabIndex={0}
            aria-label="Add tag"
        >
            <PlusIcon />
        </div>
    );

    const tagsNode = (
        <div className={s.tagList}>
            {enableDnd
                ? visibleTags.map((t) => <SortableTag key={t} id={t} />)
                : visibleTags.map((t) => {
                      const chip = (
                          <button
                              key={t}
                              type="button"
                              className={s.tag}
                              style={{ cursor: onTagAction ? 'pointer' : 'default' }}
                              onClick={(e) => {
                                  e.stopPropagation();
                              }}
                          >
                              {t}
                          </button>
                      );
                      if (!onTagAction) return chip;
                      return (
                          <Popover
                              key={t}
                              trigger="click"
                              placement="bottom"
                              overlayClassName={s.tagPopover}
                              content={
                                  <button
                                      type="button"
                                      className={s.tagPopoverAction}
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          onTagAction(t);
                                      }}
                                  >
                                      {actionText}
                                  </button>
                              }
                          >
                              {chip}
                          </Popover>
                      );
                  })}
            {hiddenCount > 0 && (
                <Tooltip title={hiddenTags.join(', ')} placement="top">
                    <button
                        type="button"
                        className={`${s.tag} ${s.tagMore}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onMoreClick?.();
                        }}
                    >
                        {hiddenCount} more
                    </button>
                </Tooltip>
            )}
            {addButton}
        </div>
    );

    if (!enableDnd) return tagsNode;

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e) => setActiveId(String(e.active.id))}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveId(null)}
        >
            <SortableContext items={tags} strategy={horizontalListSortingStrategy}>
                {tagsNode}
            </SortableContext>
            <DragOverlay>{activeId ? <span className={s.tag}>{activeId}</span> : null}</DragOverlay>
        </DndContext>
    );
}
